import React, {useEffect, useMemo, useRef, useState} from "react";
import ReactDOM from "react-dom";
import {EditorContent, useEditor} from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {ChevronDownIcon} from "@radix-ui/react-icons";
import {Bold, Check, Italic, Mail, MessageCircle, Search, Send, Users} from "lucide-react";
import {toast} from "react-toastify";
import {getPaginatedUsers} from "@/service/admin/userApi";
import {sendEmailBroadcastApi, sendWhatsAppBroadcastApi} from "@/service/admin/broadcastApi";

const PAGE_SIZE = 25;
const inputCls = "w-full rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-sm font-custom-semi-bold text-gray-800 placeholder-gray-300 outline-none transition-all duration-200 hover:border-gray-300 focus:border-[#D26A53] focus:ring-2 focus:ring-[#D26A53]/10";
const secondaryButtonCls = "inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 px-3.5 py-2 text-sm font-custom-bold text-gray-600 transition-all duration-150 hover:bg-gray-50 active:scale-95 disabled:cursor-not-allowed disabled:opacity-45";
const iconButtonCls = "flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-white hover:text-[#D26A53]";

const getTemplateBody = (template) => template?.body ?? template?.components?.find((component) => component.type?.toLowerCase() === "body")?.text ?? "";

const useDelayedClose = (isOpen, delay = 140) => {
	const [shouldRender, setShouldRender] = useState(isOpen);
	const [isClosing, setIsClosing] = useState(false);

	useEffect(() => {
		if (isOpen) {
			setShouldRender(true);
			setIsClosing(false);
		} else if (shouldRender) {
			setIsClosing(true);
			const timeoutId = setTimeout(() => {
				setShouldRender(false);
				setIsClosing(false);
			}, delay);
			return () => clearTimeout(timeoutId);
		}
	}, [delay, isOpen, shouldRender]);

	return {shouldRender, isClosing};
};

const renderTemplatePreview = (body, parameters) => {
	let output = body || "";
	parameters.forEach((value, index) => {
		output = output.replaceAll(new RegExp(`\\{\\{\\s*${index + 1}\\s*\\}\\}`, "g"), value || `{{${index + 1}}}`);
	});
	return output;
};

const countPlaceholders = (text = "") => {
	const matches = [...String(text).matchAll(/\{\{\s*(\d+)\s*\}\}/g)].map((match) => Number(match[1]));
	return matches.length ? Math.max(...matches) : 0;
};

const buildTemplateComponents = (template, parameterValues = []) => {
	if (!template?.components?.length || !parameterValues.some((value) => String(value ?? "").trim())) {
		return [];
	}

	let offset = 0;
	return template.components.reduce((components, component) => {
		const type = component.type?.toLowerCase();
		const placeholderCount = countPlaceholders(component.text ?? component.url ?? "");

		if (type === "buttons" && Array.isArray(component.buttons)) {
			component.buttons.forEach((button, index) => {
				const buttonPlaceholderCount = countPlaceholders(button.url ?? button.text ?? "");
				if (!buttonPlaceholderCount) return;

				const values = parameterValues.slice(offset, offset + buttonPlaceholderCount);
				offset += buttonPlaceholderCount;

				components.push({
					type: "button",
					sub_type: String(button.type ?? "url").toLowerCase(),
					index: String(index),
					parameters: values.map((value) => ({type: "text", text: String(value)})),
				});
			});
			return components;
		}

		if (!placeholderCount) return components;

		const values = parameterValues.slice(offset, offset + placeholderCount);
		offset += placeholderCount;

		if (type === "body" || type === "header") {
			components.push({
				type,
				parameters: values.map((value) => ({type: "text", text: String(value)})),
			});
		}

		return components;
	}, []);
};

export const SendBroadcast = ({axiosPrivate, templates}) => {
	const approvedTemplates = useMemo(
		() => templates.filter((template) => template.status === "APPROVED"),
		[templates]
	);
	const [channel, setChannel] = useState("whatsapp");
	const [selectedTemplateName, setSelectedTemplateName] = useState("");
	const [subject, setSubject] = useState("");
	const [selectedIds, setSelectedIds] = useState([]);
	const [users, setUsers] = useState([]);
	const [page, setPage] = useState(1);
	const [pagination, setPagination] = useState({totalPages: 1, totalCount: 0});
	const [globalSearch, setGlobalSearch] = useState("");
	const [loadingUsers, setLoadingUsers] = useState(false);
	const [sending, setSending] = useState(false);
	const selectedTemplate = approvedTemplates.find((template) => template.name === selectedTemplateName);
	const parameterLabels = useMemo(() => {
		if (!selectedTemplate) return [];
		const configuredLabels = selectedTemplate.config?.parameterLabels ?? [];
		const componentCount = selectedTemplate.components?.reduce(
			(total, component) => {
				if (component.type?.toLowerCase() === "buttons" && Array.isArray(component.buttons)) {
					return total + component.buttons.reduce(
						(buttonTotal, button) => buttonTotal + countPlaceholders(button.url ?? button.text ?? ""),
						0
					);
				}
				return total + countPlaceholders(component.text ?? component.url ?? "");
			},
			0
		);
		const count = componentCount || selectedTemplate.placeholderCount || 0;
		return Array.from({length: count}, (_, index) => configuredLabels[index] ?? `Parameter ${index + 1}`);
	}, [selectedTemplate]);
	const [parameters, setParameters] = useState([]);
	const [isTemplateOpen, setIsTemplateOpen] = useState(false);
	const {shouldRender: showTemplateDropdown, isClosing: templateDropdownClosing} = useDelayedClose(isTemplateOpen);
	const [templateFocusedIndex, setTemplateFocusedIndex] = useState(-1);
	const templateListRef = useRef(null);
	const templateBtnRef = useRef(null);
	const templateContainerRef = useRef(null);
	const [templateDropPos, setTemplateDropPos] = useState({top: 0, left: 0, width: 0});

	const editor = useEditor({
		extensions: [StarterKit],
		content: "<p>Namaskar {{firstName}},</p><p></p>",
		editorProps: {
			attributes: {
				class: "min-h-[160px] rounded-b-xl border border-t-0 border-gray-200 px-3.5 py-2.5 font-custom-regular text-sm outline-none transition-all duration-200 focus:border-[#D26A53] focus:ring-2 focus:ring-[#D26A53]/10",
			},
		},
	});

	useEffect(() => {
		setSelectedIds([]);
		setPage(1);
	}, [channel]);

	useEffect(() => {
		setParameters(Array.from({length: parameterLabels.length}, () => ""));
	}, [selectedTemplateName, parameterLabels.length]);

	useEffect(() => {
		if (templateFocusedIndex >= 0 && templateListRef.current) {
			templateListRef.current.children[templateFocusedIndex]?.scrollIntoView({block: "nearest"});
		}
	}, [templateFocusedIndex]);

	useEffect(() => {
		if (isTemplateOpen && templateBtnRef.current) {
			const rect = templateBtnRef.current.getBoundingClientRect();
			setTemplateDropPos({top: rect.bottom + 4, left: rect.left, width: rect.width});
		}
	}, [approvedTemplates.length, isTemplateOpen, selectedTemplateName]);

	useEffect(() => {
		const handler = (event) => {
			if (
				templateContainerRef.current
				&& !templateContainerRef.current.contains(event.target)
				&& !templateListRef.current?.contains(event.target)
			) {
				setIsTemplateOpen(false);
				setTemplateFocusedIndex(-1);
			}
		};

		document.addEventListener("mousedown", handler);
		return () => document.removeEventListener("mousedown", handler);
	}, []);

	useEffect(() => {
		const loadUsers = async () => {
			setLoadingUsers(true);
			try {
				const response = await getPaginatedUsers(axiosPrivate, {
					page,
					limit: PAGE_SIZE,
					globalSearch,
					sortField: "createdAt",
					sortOrder: "desc",
					filters: {
						[channel === "whatsapp" ? "phone.number" : "email"]: ["__not_blank__"],
					},
				});
				setUsers(response.data ?? []);
				setPagination(response.pagination ?? {totalPages: 1, totalCount: 0});
			} catch {
				toast.error("Failed to load audience.");
			} finally {
				setLoadingUsers(false);
			}
		};

		const timeoutId = setTimeout(loadUsers, 250);
		return () => clearTimeout(timeoutId);
	}, [axiosPrivate, channel, page, globalSearch]);

	const currentPageIds = users.map((user) => user._id);
	const currentPageSelected = currentPageIds.length > 0 && currentPageIds.every((id) => selectedIds.includes(id));
	const selectedCount = selectedIds.length;
	const bodyHtml = editor?.getHTML() ?? "";
	const canSend = channel === "whatsapp"
		? selectedTemplateName && selectedCount > 0 && parameters.every((value) => value.trim())
		: subject.trim() && bodyHtml.replace(/<[^>]+>/g, "").trim() && selectedCount > 0;

	const toggleCurrentPage = () => {
		if (currentPageSelected) {
			setSelectedIds((prev) => prev.filter((id) => !currentPageIds.includes(id)));
		} else {
			setSelectedIds((prev) => [...new Set([...prev, ...currentPageIds])]);
		}
	};

	const toggleRecipient = (id) => {
		setSelectedIds((prev) => prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]);
	};

	const selectTemplate = (template) => {
		setSelectedTemplateName(template.name);
		setIsTemplateOpen(false);
		setTemplateFocusedIndex(-1);
		templateBtnRef.current?.focus();
	};

	const handleTemplateKeyDown = (event) => {
		if (!isTemplateOpen) {
			if (["ArrowDown", "ArrowUp", "Enter", " "].includes(event.key)) {
				event.preventDefault();
				setIsTemplateOpen(true);
				setTemplateFocusedIndex(Math.max(approvedTemplates.findIndex((template) => template.name === selectedTemplateName), 0));
			}
			return;
		}

		switch (event.key) {
			case "ArrowDown":
				event.preventDefault();
				setTemplateFocusedIndex((index) => Math.min(index + 1, approvedTemplates.length - 1));
				break;
			case "ArrowUp":
				event.preventDefault();
				setTemplateFocusedIndex((index) => Math.max(index - 1, 0));
				break;
			case "Enter":
			case " ":
				event.preventDefault();
				if (templateFocusedIndex >= 0 && approvedTemplates[templateFocusedIndex]) {
					selectTemplate(approvedTemplates[templateFocusedIndex]);
				}
				break;
			case "Escape":
				event.preventDefault();
				setIsTemplateOpen(false);
				setTemplateFocusedIndex(-1);
				templateBtnRef.current?.focus();
				break;
			case "Tab":
				setIsTemplateOpen(false);
				setTemplateFocusedIndex(-1);
				break;
			default:
				break;
		}
	};

	const sendBroadcast = async () => {
		setSending(true);
		try {
			const response = channel === "whatsapp"
				? await sendWhatsAppBroadcastApi(axiosPrivate, {
					templateName: selectedTemplateName,
					language: selectedTemplate?.language ?? selectedTemplate?.config?.language ?? "en_US",
					recipientIds: selectedIds,
					parameters,
					components: buildTemplateComponents(selectedTemplate, parameters),
				})
				: await sendEmailBroadcastApi(axiosPrivate, {
					subject,
					body: bodyHtml,
					recipientIds: selectedIds,
				});

			toast.success(`Sent to ${response.sent ?? 0}. Failed: ${response.failed ?? 0}.`);
			if (response.failedRecipients?.length) {
				downloadFailedCsv(response.failedRecipients);
			}
			setSelectedIds([]);
		} catch (error) {
			toast.error(error?.response?.data?.message ?? "Failed to send broadcast.");
		} finally {
			setSending(false);
		}
	};

	return (
		<div className="grid h-full min-h-0 gap-3 xl:grid-cols-[1fr_1.1fr_1fr]">
			<section className="min-h-0 overflow-auto rounded-xl border border-gray-200 bg-white p-4">
				<h2 className="font-custom-bold text-lg text-gray-950">Send Broadcast</h2>
				<div className="mt-4 grid gap-3 sm:grid-cols-2">
					<ChannelCard active={channel === "whatsapp"} icon={<MessageCircle size={20}/>} title="WhatsApp" onClick={() => setChannel("whatsapp")}/>
					<ChannelCard active={channel === "email"} icon={<Mail size={20}/>} title="Email" onClick={() => setChannel("email")}/>
				</div>

				{channel === "whatsapp" ? (
					<div className="mt-5 space-y-4">
						<label className="block">
							<span className="font-custom-semi-bold text-sm text-gray-700">Approved template</span>
							<div ref={templateContainerRef} className="relative mt-1">
								<button
									ref={templateBtnRef}
									type="button"
									role="combobox"
									aria-controls="broadcast-template-listbox"
									aria-expanded={isTemplateOpen ? "true" : "false"}
									aria-haspopup="listbox"
									disabled={!approvedTemplates.length}
									onClick={() => {
										setIsTemplateOpen((value) => !value);
										setTemplateFocusedIndex(isTemplateOpen ? -1 : Math.max(approvedTemplates.findIndex((template) => template.name === selectedTemplateName), 0));
									}}
									onKeyDown={handleTemplateKeyDown}
									className="flex w-full items-center justify-between rounded-xl border bg-white px-3.5 py-2.5 text-left text-sm font-custom-semi-bold transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60"
									style={{
										borderColor: isTemplateOpen ? "#D26A53" : "#E5E7EB",
										boxShadow: isTemplateOpen ? "0 0 0 3px rgba(210,106,83,0.10)" : "none",
										color: selectedTemplate ? "#1F2937" : "#D1D5DB",
									}}
								>
									<span className="min-w-0 flex-1 truncate">
										{selectedTemplate ? `${selectedTemplate.name} (${selectedTemplate.language})` : "Select template"}
									</span>
									<ChevronDownIcon className={`ml-2 shrink-0 text-gray-400 transition-transform duration-200 ${isTemplateOpen ? "rotate-180" : ""}`}/>
								</button>

								{showTemplateDropdown && ReactDOM.createPortal(
									<div
										style={{
											position: "fixed",
											top: templateDropPos.top,
											left: templateDropPos.left,
											width: templateDropPos.width,
											zIndex: 9999,
										}}
										className={`${templateDropdownClosing ? "dropdown-close" : "dropdown-open"} overflow-hidden rounded-xl border border-gray-100 bg-white shadow-xl`}
									>
										<div
											ref={templateListRef}
											id="broadcast-template-listbox"
											role="listbox"
											onWheel={(event) => event.stopPropagation()}
											className="max-h-56 overflow-y-auto"
										>
											{approvedTemplates.map((template, index) => {
												const selected = selectedTemplateName === template.name;
												return (
													<button
														key={`${template.name}-${template.language}`}
														type="button"
														role="option"
														aria-selected={selected}
														onMouseEnter={() => setTemplateFocusedIndex(index)}
														onClick={() => selectTemplate(template)}
														className={`flex w-full items-center justify-between gap-3 px-3.5 py-2.5 text-left font-custom-medium text-[0.78rem] transition-colors
															${templateFocusedIndex === index ? "bg-orange-50" : "hover:bg-gray-50"}
															${selected ? "font-custom-semi-bold text-[#D26A53]" : "text-gray-700"}`}
													>
														<span className="min-w-0 flex-1 truncate">{template.name} ({template.language})</span>
														{selected && <Check className="h-3.5 w-3.5 shrink-0 text-[#D26A53]"/>}
													</button>
												);
											})}
										</div>
									</div>,
									document.body
								)}
							</div>
						</label>
						{selectedTemplate && (
							<div className="rounded-xl bg-gray-50 p-3">
								<p className="font-custom-bold text-sm text-gray-950">{selectedTemplate.config?.description || selectedTemplate.name}</p>
								<p className="mt-1 font-custom-regular text-xs text-gray-500">{selectedTemplate.config?.targetAudience}</p>
								<p className="mt-3 whitespace-pre-wrap rounded-lg bg-white p-3 font-custom-regular text-sm text-gray-700 ring-1 ring-gray-200">
									{getTemplateBody(selectedTemplate)}
								</p>
							</div>
						)}
					</div>
				) : (
					<div className="mt-5 space-y-4">
						<label className="block">
							<span className="font-custom-semi-bold text-sm text-gray-700">Subject</span>
							<input className={`${inputCls} mt-1`} value={subject} onChange={(event) => setSubject(event.target.value)} placeholder="Broadcast subject"/>
						</label>
						<div>
							<span className="font-custom-semi-bold text-sm text-gray-700">Body</span>
							<div className="mt-1 flex rounded-t-xl border border-gray-200 bg-gray-50 p-1">
								<button type="button" onClick={() => editor?.chain().focus().toggleBold().run()} className={iconButtonCls} aria-label="Bold"><Bold size={16}/></button>
								<button type="button" onClick={() => editor?.chain().focus().toggleItalic().run()} className={iconButtonCls} aria-label="Italic"><Italic size={16}/></button>
							</div>
							<EditorContent editor={editor}/>
						</div>
					</div>
				)}
			</section>

			<section className="flex min-h-0 flex-col rounded-xl border border-gray-200 bg-white">
				<div className="border-b border-gray-100 p-4">
					<div className="flex items-center justify-between gap-3">
						<div>
							<h2 className="font-custom-bold text-lg text-gray-950">Select Audience</h2>
							<p className="font-custom-regular text-xs text-gray-500">{channel === "whatsapp" ? "Phone required" : "Email required"} filter is locked.</p>
						</div>
						<span className="rounded-full bg-[#A20000]/10 px-3 py-1 font-custom-bold text-xs text-[#A20000]">
							{selectedCount} selected
						</span>
					</div>
					<div className="relative mt-3">
						<Search size={16} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-300"/>
						<input className={`${inputCls} pl-10`} value={globalSearch} onChange={(event) => {
							setPage(1);
							setGlobalSearch(event.target.value);
						}} placeholder="Search name, email, or phone"/>
					</div>
				</div>
				<div className="min-h-0 flex-1 overflow-auto">
					<table className="min-w-full divide-y divide-gray-200 text-left">
						<thead className="sticky top-0 bg-gray-50">
						<tr className="text-xs font-custom-bold uppercase text-gray-500">
							<th className="px-3 py-3"><input type="checkbox" className="h-4 w-4 accent-[#D26A53]" checked={currentPageSelected} onChange={toggleCurrentPage}/></th>
							<th className="px-3 py-3">Name</th>
							<th className="px-3 py-3">Contact</th>
						</tr>
						</thead>
						<tbody className="divide-y divide-gray-100">
						{loadingUsers ? (
							<tr><td colSpan={3} className="px-3 py-8 text-center font-custom-medium text-gray-500">Loading audience...</td></tr>
						) : users.length === 0 ? (
							<tr><td colSpan={3} className="px-3 py-8 text-center font-custom-medium text-gray-500">No matching recipients.</td></tr>
						) : users.map((user) => (
							<tr key={user._id} className="text-sm">
								<td className="px-3 py-3"><input type="checkbox" className="h-4 w-4 accent-[#D26A53]" checked={selectedIds.includes(user._id)} onChange={() => toggleRecipient(user._id)}/></td>
								<td className="px-3 py-3">
									<p className="font-custom-semi-bold text-gray-900">{[user.firstName, user.middleName, user.lastName].filter(Boolean).join(" ") || user._id}</p>
									<p className="font-custom-regular text-xs text-gray-500">{user.familyTreeName ?? ""}</p>
								</td>
								<td className="px-3 py-3 font-custom-regular text-gray-600">
									{channel === "whatsapp" ? `${user.phone?.dialingCode ?? ""} ${user.phone?.number ?? ""}` : user.email}
								</td>
							</tr>
						))}
						</tbody>
					</table>
				</div>
				<div className="flex items-center justify-between border-t border-gray-100 p-3">
					<p className="font-custom-regular text-xs text-gray-500">{pagination.totalCount ?? 0} eligible recipients</p>
					<div className="flex gap-2">
						<button type="button" disabled={page <= 1} onClick={() => setPage((prev) => prev - 1)} className={`${secondaryButtonCls} px-3 py-1.5 text-xs`}>Previous</button>
						<button type="button" disabled={page >= pagination.totalPages} onClick={() => setPage((prev) => prev + 1)} className={`${secondaryButtonCls} px-3 py-1.5 text-xs`}>Next</button>
					</div>
				</div>
			</section>

			<section className="min-h-0 overflow-auto rounded-xl border border-gray-200 bg-white p-4">
				<div className="flex items-center gap-2">
					<Users size={18} className="text-[#A20000]"/>
					<h2 className="font-custom-bold text-lg text-gray-950">Preview & Send</h2>
				</div>
				{selectedCount > (channel === "whatsapp" ? 500 : 200) && (
					<p className="mt-3 rounded-lg bg-amber-50 p-3 font-custom-medium text-sm text-amber-700">
						Large selection warning: synchronous sends may take a while.
					</p>
				)}

				{channel === "whatsapp" ? (
					<div className="mt-4 space-y-4">
						{parameterLabels.map((label, index) => (
							<label key={index} className="block">
								<span className="font-custom-semi-bold text-sm text-gray-700">{label || `Parameter ${index + 1}`}</span>
								<input className={`${inputCls} mt-1`} value={parameters[index] ?? ""} onChange={(event) => {
									const next = [...parameters];
									next[index] = event.target.value;
									setParameters(next);
								}}/>
							</label>
						))}
						<div className="rounded-2xl bg-[#e5ddd5] p-4">
							<p className="ml-auto max-w-[90%] whitespace-pre-wrap rounded-xl bg-[#dcf8c6] p-3 font-custom-regular text-sm text-gray-800 shadow">
								{renderTemplatePreview(getTemplateBody(selectedTemplate), parameters) || "Select a template to preview."}
							</p>
						</div>
					</div>
				) : (
					<div className="mt-4 rounded-xl border border-gray-200 p-4">
						<p className="font-custom-regular text-xs text-gray-500">From: {process.env.REACT_APP_SMTP_FROM ?? "info@joglekarkul.com"}</p>
						<h3 className="mt-2 font-custom-bold text-gray-950">{subject || "Subject preview"}</h3>
						<div className="prose prose-sm mt-3 max-w-none font-custom-regular" dangerouslySetInnerHTML={{__html: bodyHtml}}/>
					</div>
				)}

				<button
					type="button"
					disabled={!canSend || sending}
					onClick={sendBroadcast}
					className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 font-custom-bold text-white transition-all duration-150 hover:opacity-90 active:scale-95 disabled:cursor-not-allowed disabled:opacity-60"
					style={{
						background: (!canSend || sending) ? "#D1D5DB" : "linear-gradient(135deg, #D26A53, #C05A43)",
						boxShadow: (!canSend || sending) ? "none" : "0 4px 14px rgba(210,106,83,0.35)",
					}}
				>
					<Send size={18}/> {sending ? "Sending..." : `Send to ${selectedCount} recipients`}
				</button>
			</section>
		</div>
	);
};

const ChannelCard = ({active, icon, title, onClick}) => (
	<button
		type="button"
		onClick={onClick}
		className="flex w-full items-center gap-3 rounded-xl p-4 text-left transition-all duration-200 active:scale-95"
		style={active
			? {background: "#FDF5F3", border: "2px solid #F5CEC7", color: "#D26A53", boxShadow: "0 2px 8px rgba(0,0,0,0.06)"}
			: {background: "#FAFAFA", border: "2px solid #E5E7EB", color: "#6B7280"}}
	>
		{icon}
		<span className="flex-1 font-custom-bold text-sm">{title}</span>
		<span
			className="h-4 w-4 rounded-full transition-all duration-200"
			style={{
				background: active ? "#D26A53" : "#F3F4F6",
				border: `2px solid ${active ? "#D26A53" : "#E5E7EB"}`,
			}}
		/>
	</button>
);

const downloadFailedCsv = (failedRecipients) => {
	const header = "recipientId,reason";
	const rows = failedRecipients.map((item) => `"${item.recipientId ?? ""}","${String(item.reason ?? "").replaceAll("\"", "\"\"")}"`);
	const blob = new Blob([[header, ...rows].join("\n")], {type: "text/csv;charset=utf-8;"});
	const url = window.URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.setAttribute("download", `broadcast-failures-${new Date().toISOString().slice(0, 10)}.csv`);
	document.body.appendChild(link);
	link.click();
	link.remove();
	window.URL.revokeObjectURL(url);
};
